import { addHours } from 'date-fns';
import addMinutes from 'date-fns/addMinutes';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';

import { DateFormatsEnum, DEFAULT_TIMEZONE, MIN_BLUEBIRD_DATE } from '../constants/date';
import { ITimezone } from '../types/timezone';
import { timezones } from './../assets/data/timezones';

export const isValidDate = (date: Date | number) => isAfter(date, MIN_BLUEBIRD_DATE);

export const formatDate = (dateString: string, dateFormat = DateFormatsEnum.MM_dd_yyyy) => {
  const utcDate = getUTCDate(dateString);

  if (isValidDate(new Date(utcDate))) {
    return format(utcDate, dateFormat);
  }

  return '';
};

export const getUTCDate = (dateString: string): Date => {
  const date = new Date(dateString);
  return addMinutes(date, date.getTimezoneOffset());
};

export const isDateInFuture = (dateString: string): boolean => {
  if (!isValidDate(new Date(dateString))) {
    return true;
  }

  return isAfter(new Date(dateString), new Date());
};

export const isDST = (timezone: string): boolean => {
  const now = new Date();
  const jan = new Date(now.getFullYear(), 0, 1);
  const jul = new Date(now.getFullYear(), 6, 1);

  const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  const currentOffset = now.getTimezoneOffset();

  return currentOffset < stdOffset;
};

export const detectUserTimezone = (): ITimezone => {
  const offset = -new Date().getTimezoneOffset() / 60;
  const matched = timezones.filter((tz) => tz.offset === offset);

  if (matched.length === 0) {
    return DEFAULT_TIMEZONE;
  }

  const locale = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isDSTActive = isDST(locale);

  const locationMatch = matched.find((tz) => {
    return tz.utc.some((loc) => loc.includes(locale)) && tz.isdst === isDSTActive;
  });
  return locationMatch || matched[0];
};

export const getRoundedMinTime = (timezone: ITimezone): Date => {
  const now = new Date();
  const userOffset = -now.getTimezoneOffset();

  now.setMinutes(now.getMinutes() + 60);
  now.setSeconds(0, 0);
  now.setHours(now.getHours() + 1);
  now.setMinutes(0);

  const hasFractionalOffset = Math.floor(timezone.offset) === Math.ceil(timezone.offset);
  now.setHours((now.getHours() + timezone.offset - Math.floor(userOffset / 60) + (hasFractionalOffset ? 0 : 1)) % 24);
  return now;
};

export const getMinTimeForSelectedDate = (selectedDate: Date | null, timezone: ITimezone): Date | undefined => {
  if (!selectedDate) {
    return;
  }

  let today = new Date();
  today = addHours(today, timezone.offset);
  today.setHours(0, 0, 0, 0);

  const isToday = selectedDate.setHours(0, 0, 0, 0) === today.getTime();
  return isToday ? getRoundedMinTime(timezone) : undefined;
};

export const getRoundedMinDate = (timezone: ITimezone): Date => {
  let now = new Date();
  now = addHours(now, timezone.offset);
  now.setHours(0, 0, 0, 0);
  return now;
};
